import { AxiosError } from 'axios';
import { api } from '@shared/api';
import { mapSenseContexts } from '@shared/lib';
import type { Level, Word, WordMeaning, WordState } from '@shared/types';

interface BackendTranslation {
  id: string;
  text: string;
  gender: string | null;
}

interface BackendSense {
  id: string;
  translations: BackendTranslation[];
  en_synonyms: string[];
  example_en: string | null;
  simple_example_en?: string | null;
  hint: string | null;
  definition: string | null;
  selection_options: string[] | null;
  is_rare?: boolean;
  contexts?: unknown;
}

interface BackendMeaning {
  id: string;
  pos: string | null;
  senses: BackendSense[];
}

interface BackendCard {
  id: string;
  front_name: string;
  /** Фразовый глагол — помечается при генерации (2026-10-06). */
  is_phrasal_verb?: boolean;
  primary_translation: string | null;
  transcription: string | null;
  cefr: Level | null;
  meanings: BackendMeaning[];
  // pending | in_progress | completed | rejected | failed | manual
  status?: string;
  is_need_error_action?: boolean;
  // string — текущий формат; string[] — устаревший формат (char-массив до миграции).
  error_info?: string | string[] | null;
  expect_word?: string[] | null;
}

function mapMeanings(meanings: BackendMeaning[] | null | undefined): WordMeaning[] {
  if (!Array.isArray(meanings)) return [];
  return meanings.map((m) => ({
    id: m.id,
    pos: m.pos ?? null,
    senses: (m.senses ?? []).map((s) => ({
      id: s.id,
      translations: (s.translations ?? []).map((t) => ({
        id: t.id,
        text: t.text,
        gender: t.gender ?? null,
      })),
      enSynonyms: s.en_synonyms ?? [],
      exampleEn: s.example_en ?? null,
      simpleExampleEn: s.simple_example_en ?? null,
      hint: s.hint ?? null,
      definition: s.definition ?? null,
      selectionOptions: s.selection_options ?? null,
      isRare: s.is_rare ?? false,
      contexts: mapSenseContexts(s.contexts),
    })),
  }));
}

function pickPrimary(card: BackendCard): string {
  if (card.primary_translation?.trim()) return card.primary_translation.trim();
  return card.meanings?.[0]?.senses?.[0]?.translations?.[0]?.text?.trim() ?? '';
}

function normalizeErrorInfo(value: string | string[] | null | undefined): string | null {
  if (value == null) return null;
  if (typeof value === 'string') return value.trim() || null;
  if (Array.isArray(value)) {
    // Старые данные: AI возвращал строку, Postgres ARRAY раскладывал её по символам.
    // Склеиваем обратно — лучше, чем рендерить столбик символов.
    const joined = value.join('').trim();
    return joined || null;
  }
  return null;
}

function normalizeExpectWord(value: string[] | null | undefined): string[] | null {
  if (value == null) return null;
  if (!Array.isArray(value) || value.length === 0) return null;

  // Детект корраптных данных: AI вернул строку типа "{summer,hose}" вместо массива,
  // и Postgres ARRAY разложил её посимвольно. Признак — все элементы длиной 1 и
  // начинается с "{", заканчивается на "}".
  const looksCharSplit =
    value.length >= 3 &&
    value[0] === '{' &&
    value[value.length - 1] === '}' &&
    value.every((c) => typeof c === 'string' && c.length <= 1);

  if (looksCharSplit) {
    const inner = value.slice(1, -1).join('');
    return inner
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
  }

  return value.filter((v) => typeof v === 'string' && v.trim().length > 0);
}

interface BackendUserStateCard {
  // id конкретного user_card_state (surrogate, уникален per-pos). card_id общий у
  // разных значений одного слова — для выбора карточек в тренировке нужен именно id.
  id: string;
  card_id: string;
  meaning_id: string | null;
  state: number;
  step: number | null;
  stability: number | null;
  difficulty: number | null;
  due: string;
  last_review: string | null;
  reps: number;
  lapses: number;
  is_hidden?: boolean;
  session_sense_ids?: string[];
  card: BackendCard;
}

function fsrsStateToWordState(state: number): WordState {
  if (state === 0) return 'new';
  if (state === 2) return 'learned';
  return 'review';
}

function mapBackendCard(c: BackendUserStateCard): Word {
  return {
    id: c.card_id,
    stateId: c.id,
    meaningId: c.meaning_id ?? undefined,
    word: c.card.front_name,
    ipa: c.card.transcription ?? '',
    ru: pickPrimary(c.card),
    state: fsrsStateToWordState(c.state),
    isNeedAction: c.card.is_need_error_action ?? false,
    isManual: c.card.status === 'manual',
    isPhrasalVerb: !!c.card.is_phrasal_verb,
    isHidden: c.is_hidden ?? false,
    errorInfo: normalizeErrorInfo(c.card.error_info),
    expectWord: normalizeExpectWord(c.card.expect_word),
    meanings: mapMeanings(c.card.meanings),
    sessionSenseIds: c.session_sense_ids ?? [],
  };
}

/** Галочки смыслов карточки (state): каждый отмеченный смысл — отдельный показ в
 *  тренировке. Бэк возвращает отмеченные смыслы в порядке карточки. */
export async function saveSessionSenses(stateId: string, senseIds: string[]): Promise<string[]> {
  const { data } = await api.put<{ sense_ids: string[] }>(
    `/cards/states/${stateId}/session-senses`,
    { sense_ids: senseIds },
  );
  return data.sense_ids ?? senseIds;
}

/** Пометка строки «Слов» — по истории тренировок, как «Повторить N слов» у модуля (бэкенд, 2026-10-28):
 *  new — не тренировали; repeat — ошибка в прошлый раз или давно не встречалось; today — отвечали
 *  сегодня; fresh — недавно и без ошибки. */
export type WordTraining = 'new' | 'repeat' | 'today' | 'fresh';
export type MyWordsKind = 'all' | 'new' | 'repeat';

/** Слово ученика на странице «Слова и модули»: карточка как в модуле, плюс где лежит и пометка. */
export interface MyWord extends Word {
  stateId: string;
  module: { id: string; title: string };
  training: WordTraining;
  /** Последнее действие с карточкой (создание, повторное добавление, правка переводов) — порядок списка. */
  touchedAt: string | null;
}

export interface MyWordsPage {
  items: MyWord[];
  total: number;
  page: number;
  pages: number;
  /** Счётчики чипов — по найденному поиском, до фильтра. */
  counts: { all: number; new: number; repeat: number };
}

interface BackendMyWord extends BackendUserStateCard {
  touched_at?: string | null;
  module: { id: string; title: string };
  training: WordTraining;
}

interface BackendMyWordsPage {
  items: BackendMyWord[];
  total: number;
  page: number;
  pages: number;
  counts: { all: number; new: number; repeat: number };
}

/** GET /cards/words — все слова ученика, последние изменения сверху (2026-10-28). */
export async function fetchMyWords(params: {
  page: number;
  size: number;
  q?: string;
  kind?: MyWordsKind;
  /** Только слова этого модуля — модуль выбран слева (2026-10-28). */
  moduleId?: string;
}): Promise<MyWordsPage> {
  const { data } = await api.get<BackendMyWordsPage>('/cards/words', {
    params: {
      page: params.page,
      size: params.size,
      ...(params.q ? { q: params.q } : {}),
      ...(params.kind && params.kind !== 'all' ? { kind: params.kind } : {}),
      ...(params.moduleId ? { module_id: params.moduleId } : {}),
    },
  });
  return {
    items: data.items.map((item) => ({
      ...mapBackendCard(item),
      stateId: item.id,
      module: item.module,
      training: item.training,
      touchedAt: item.touched_at ?? null,
    })),
    total: data.total,
    page: data.page,
    pages: data.pages,
    counts: data.counts,
  };
}

/** Добавленное сегодня и ещё не тренированное (2026-10-28): записи «Сегодня» и «Тренировать новые». */
export interface FreshWords {
  /** Записи нажатий «Создать», где осталось что тренировать, — новые сверху. */
  generationIds: string[];
  /** Модуль → карточки (user_card_state.id), последние изменения сверху. */
  byModule: Record<string, string[]>;
}

interface BackendFreshWords {
  generation_ids: string[];
  modules: { module_id: string; state_ids: string[] }[];
}

/** GET /cards/words/fresh — карточки сегодняшних (МСК) нажатий «Создать», которые ещё не тренировали: только
 *  выбранные значения, без прежних значений того же слова. */
export async function fetchFreshWords(): Promise<FreshWords> {
  const { data } = await api.get<BackendFreshWords>('/cards/words/fresh');
  return {
    generationIds: data.generation_ids,
    byModule: Object.fromEntries(data.modules.map((m) => [m.module_id, m.state_ids])),
  };
}

export async function fetchWordsForUnit(moduleId: string): Promise<Word[]> {
  const { data } = await api.get<BackendUserStateCard[]>(`/cards/modules/${moduleId}`);
  return data.map(mapBackendCard);
}

/**
 * Скрыть / восстановить карточку (конкретную часть речи) в модуле — soft-hide
 * вместо удаления. meaning_id обязателен (скрываем real-state). 204 при успехе;
 * идемпотентно на бэке. meaning_id шлём query-параметром (как DELETE /cards/{id}).
 */
export async function hideWord(cardId: string, meaningId: string): Promise<void> {
  await api.post(`/cards/${cardId}/hide`, undefined, { params: { meaning_id: meaningId } });
}

export async function restoreWord(cardId: string, meaningId: string): Promise<void> {
  await api.post(`/cards/${cardId}/restore`, undefined, { params: { meaning_id: meaningId } });
}

export type AddCardErrorCode = 'ALREADY_ADDED' | 'EMPTY_FIELDS' | 'NETWORK' | 'UNKNOWN';

export class AddCardError extends Error {
  constructor(public code: AddCardErrorCode) {
    super(code);
  }
}

export async function addCard(payload: { word: string; unitId: string }): Promise<void> {
  const front = payload.word.trim();
  if (!front) throw new AddCardError('EMPTY_FIELDS');
  try {
    await api.post('/cards/create', {
      module_id: payload.unitId,
      front_name: front,
    });
  } catch (err) {
    if (err instanceof AxiosError) {
      if (!err.response) throw new AddCardError('NETWORK');
      if (err.response.status === 409) throw new AddCardError('ALREADY_ADDED');
    }
    throw new AddCardError('UNKNOWN');
  }
}
